// Drama Pie 契约的唯一真源。
//
// 为什么用 zod 而不是 TypeSpec / OpenAPI 手写：
//   同一份定义既做后端运行时校验，又给前端 `z.infer` 出类型，不存在「文档和实现漂移」。
//   需要 OpenAPI 时从 zod 生成即可，不额外维护一份 YAML。
//
// 约定（见 docs/02-开发/API-契约-M1.md）：
//   · 时间为 Unix 毫秒整数
//   · ID 带前缀：prj_ ep_ scn_ sht_ chr_ loc_ prp_ ast_ job_
//   · 缺字段用 null，不省略 key
//   · 错误体只有一种形状：{ error: { code, message, details? } }
import { z } from "zod";

// ---------------------------------------------------------------- 基础

export const IdPrefix = z.enum(["prj", "ep", "scn", "sht", "chr", "loc", "prp", "ast", "job", "exp", "ckp", "rec", "shr", "wld", "out", "spt", "trm"]);
export type IdPrefix = z.infer<typeof IdPrefix>;

/** 带前缀的 ID：`prj_8f3a…` */
// 前缀必填，下划线可选，后缀 2–64 位。
// 允许 `ep01` 与 `ep_01` 两种写法：短 id 走前者（目录名 `episodes/ep01/` 更好读），
// 带随机串的走后者（`prj_8f3a…`）。文档里的示例两种都有，所以不能只认一种。
export const PrefixedId = z
  .string()
  .regex(/^(prj|ep|scn|sht|chr|loc|prp|ast|job|exp|ckp|rec|shr|wld|out|spt|trm)_?[A-Za-z0-9][A-Za-z0-9_-]{1,63}$/, "invalid id");
export type PrefixedId = z.infer<typeof PrefixedId>;

export const UnixMs = z.number().int().nonnegative();

export const Aspect = z.enum(["9:16", "16:9", "1:1"]);
export type Aspect = z.infer<typeof Aspect>;

export const Style = z.enum(["live_action", "anime"]);
export type Style = z.infer<typeof Style>;

// ---------------------------------------------------------------- 错误

export const ErrorCode = z.enum([
  "bad_request",
  "validation_failed",
  "unauthorized",
  "session_expired",
  "forbidden",
  "quota_exceeded",
  "insufficient_points",
  "project_not_found",
  "file_not_found",
  "shot_not_found",
  "scene_not_found",
  "repo_not_found",
  "revision_conflict",
  /** 回滚到已被保留策略挤掉（或从未存在）的版本（M4）。 */
  "revision_not_found",
  "entity_in_use",
  /** 产出线还有集挂靠（M8）：先搬走或删掉集再删线，409。 */
  "output_in_use",
  /** 产出线不存在（M8）。 */
  "output_not_found",
  "duplicate_request",
  /** 账号还没有绑定 miyaplan（billing 刷新余额时 409）：先登录/开通再刷新。 */
  "miya_not_connected",
  "unsupported_view",
  "not_implemented",
  "not_configured",
  "upstream_unavailable",
  "internal",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiErrorBody = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/** 无响应体的写操作统一返回它（如 logout） */
export const OkSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof OkSchema>;

// ---------------------------------------------------------------- 账号

export const MiyaPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  expiresAt: UnixMs.nullable(),
});
export type MiyaPlan = z.infer<typeof MiyaPlanSchema>;

/** 权益目录项：slug 是机器契约，name 是兜底文案，i18nKey 命中时优先 */
export const EntitlementCatalogItem = z.object({
  slug: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  name: z.string(),
  i18nKey: z.string().optional(),
  type: z.enum(["bytes", "count", "bool"]),
  unit: z.string().optional(),
  /** defaults 必须覆盖所有 required=true 的 slug（miyaplan 侧配置校验用） */
  required: z.boolean().optional(),
});
export type EntitlementCatalogItem = z.infer<typeof EntitlementCatalogItem>;

export const MeSchema = z.object({
  accountId: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  picture: z.string().nullable(),
  miya: z.object({
    connected: z.boolean(),
    workspaceId: z.string().nullable(),
    /** 购买页绝对地址（由服务端按上游计费端域名拼好）；未连接 / 无工作区时为 null。 */
    purchaseUrl: z.string().nullable(),
    usablePoints: z.number().int(),
    plan: MiyaPlanSchema.nullable(),
    entitlements: z.record(z.string(), z.union([z.number(), z.boolean()])),
    catalog: z.array(EntitlementCatalogItem),
  }),
  quota: z.object({
    mediaBytes: z.number().int(),
    mediaLimit: z.number().int(),
    projects: z.number().int(),
    projectLimit: z.number().int(),
  }),
});
export type Me = z.infer<typeof MeSchema>;

// ---------------------------------------------------------------- 跨站产品菜单（P1-W3/W4）

/** 菜单里的链接只允许 http(s)：前端会把它直接放进 `<a href>`，不能让上游塞进 javascript:。 */
function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * 一条跨站导航（Plan `GET /api/nav?w=<workspace>` 的 item，Studio 原样代理）。
 * `host` 用来在菜单里标记「当前站点」：与 `window.location.host` 逐字比较。
 */
export const NavItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  url: z.string().min(1).refine(isHttpUrl, "nav url must be http(s)"),
  host: z.string().min(1),
});
export type NavItem = z.infer<typeof NavItemSchema>;

export const NavMenuSchema = z.object({ items: z.array(NavItemSchema) });
export type NavMenu = z.infer<typeof NavMenuSchema>;

/**
 * 后端兜底菜单（Plan 不可达 / 返回空 / 坏载荷时用）。
 *
 * 放在 shared 是为了两边共用同一份：后端保证菜单永不消失，前端在请求本身失败时
 * （断网、SW 异常）也有同样的三条可点。
 */
export const NAV_FALLBACK_ITEMS: readonly NavItem[] = [
  { id: "studio", name: "工作室", url: "https://studio.dramapie.com/", host: "studio.dramapie.com" },
  { id: "canvas", name: "画布", url: "https://canvas.dramapie.com/", host: "canvas.dramapie.com" },
  { id: "plan", name: "账户", url: "https://plan.dramapie.com/", host: "plan.dramapie.com" },
];

// ---------------------------------------------------------------- 项目

export const ProjectSummary = z.object({
  id: PrefixedId,
  title: z.string(),
  coverUrl: z.string().nullable(),
  episodeCount: z.number().int(),
  shotCount: z.number().int(),
  updatedAt: UnixMs,
});
export type ProjectSummary = z.infer<typeof ProjectSummary>;

export const ProjectList = z.object({
  items: z.array(ProjectSummary),
  nextCursor: z.string().optional(),
});
export type ProjectList = z.infer<typeof ProjectList>;

export const CreateProjectInput = z.object({
  title: z.string().min(1).max(60),
  aspect: Aspect.default("9:16"),
  style: Style.default("live_action"),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const CreateProjectOutput = z.object({
  id: PrefixedId,
  title: z.string(),
  createdAt: UnixMs,
});
export type CreateProjectOutput = z.infer<typeof CreateProjectOutput>;

// ---------------------------------------------------------------- 产出线（M8）
//
// 「一个项目可以有多种形态的产出」：短剧 / 微电影 / 条漫 / 动画版 / MV。
// 每集归属于一条产出线，左栏切换产出线 = 切换集列表。

export const OutputKind = z.enum(["drama", "film", "comic", "anim", "mv"]);
export type OutputKind = z.infer<typeof OutputKind>;

/** 契约 M8 §1 冻结：产出线画幅只有竖屏 / 横屏两种。 */
export const OutputAspect = z.enum(["9:16", "16:9"]);
export type OutputAspect = z.infer<typeof OutputAspect>;

export const OutputLine = z.object({
  id: PrefixedId,
  title: z.string().min(1).max(40),
  kind: OutputKind,
  aspect: OutputAspect,
  /** 排序键：小的在前（同 order 时按 id 稳定排）。 */
  order: z.number().int(),
  /** 隐藏：不在切换器主列表里，但数据保留、不锁写。 */
  hidden: z.boolean(),
  /** 归档：只读，可在「已隐藏 / 归档」里恢复。 */
  archived: z.boolean(),
});
export type OutputLine = z.infer<typeof OutputLine>;

export const OutputList = z.object({ items: z.array(OutputLine) });
export type OutputList = z.infer<typeof OutputList>;

export const CreateOutputInput = z.object({
  title: z.string().min(1).max(40),
  kind: OutputKind,
  aspect: OutputAspect.default("9:16"),
});
export type CreateOutputInput = z.infer<typeof CreateOutputInput>;

/**
 * PATCH 的字段清单（M8 §2 + 补充）。
 *
 * `kind` / `aspect` 可改：建错形态时只能删了重建、而删除又受「还有 N 集」限制，
 * 等于卡死；改成同一项目内的另一种形态不会破坏已有数据（画幅只影响生成/导出比例）。
 *
 * 归档是**只读开关**：置为 `archived: true` 的那一次不能同时改其它字段；
 * 已归档的线在恢复（`archived: false`）之前也不能改（服务端 422）。
 */
export const PatchOutputInput = z.object({
  title: z.string().min(1).max(40).optional(),
  kind: OutputKind.optional(),
  aspect: OutputAspect.optional(),
  hidden: z.boolean().optional(),
  archived: z.boolean().optional(),
  order: z.number().int().optional(),
});
export type PatchOutputInput = z.infer<typeof PatchOutputInput>;

/**
 * 把某一集移到别的产出线（M8 §3 的「删线前先把集移走」唯一入口）。
 *
 * 为什么单独开一条而不是塞进 PATCH output：集是项目文档（storyboard）里的东西，
 * 它有自己的提交闸门；放在产出线的 PATCH 里会让「改线」和「改集」混成一次提交。
 */
export const PatchEpisodeInput = z.object({
  outputId: PrefixedId,
});
export type PatchEpisodeInput = z.infer<typeof PatchEpisodeInput>;

export const EpisodeRef = z.object({
  id: PrefixedId,
  no: z.number().int(),
  title: z.string(),
  sceneCount: z.number().int(),
  shotCount: z.number().int(),
  /** 归属的产出线（M8）：缺省时服务端回落到第一条产出线，永远有值。 */
  outputId: PrefixedId,
});
export type EpisodeRef = z.infer<typeof EpisodeRef>;

export const OutputRef = z.object({ id: z.string(), title: z.string() });
export type OutputRef = z.infer<typeof OutputRef>;

export const ProjectDetail = z.object({
  id: PrefixedId,
  title: z.string(),
  aspect: Aspect,
  style: Style,
  episodes: z.array(EpisodeRef),
  outputs: z.array(OutputRef),
  rev: z.number().int(),
  updatedAt: UnixMs,
});
export type ProjectDetail = z.infer<typeof ProjectDetail>;

export const PatchProjectInput = z.object({
  title: z.string().min(1).max(60).optional(),
});
export type PatchProjectInput = z.infer<typeof PatchProjectInput>;

// ---------------------------------------------------------------- 故事板投影

export const DialogueLine = z.object({
  who: z.string(),
  text: z.string(),
  type: z.enum(["speech", "thought", "vo"]),
});
export type DialogueLine = z.infer<typeof DialogueLine>;

export const MediaRef = z.object({
  kind: z.enum(["image", "video"]),
  key: z.string(),
  url: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type MediaRef = z.infer<typeof MediaRef>;

export const ShotCheck = z.object({ q: z.string(), pass: z.boolean() });
export type ShotCheck = z.infer<typeof ShotCheck>;

export const ShotStatus = z.enum(["draft", "keyframe_ready", "approved", "failed"]);
export type ShotStatus = z.infer<typeof ShotStatus>;

export const ShotView = z.object({
  id: PrefixedId,
  order: z.number().int(),
  sceneId: PrefixedId,
  framing: z.string(),
  duration: z.number(),
  status: ShotStatus,
  action: z.string(),
  dialogue: z.array(DialogueLine),
  chars: z.array(PrefixedId),
  keyframe: MediaRef.nullable(),
  endFrame: MediaRef.nullable(),
  clip: MediaRef.nullable(),
  checks: z.array(ShotCheck),
  costPoints: z.number().int(),
  rev: z.number().int(),
  /**
   * 画布 hover 用的提示词预览（~600 字符，投影时算，只读）。
   *
   * 与生成时**同一套** `buildShotPrompt`：风格 → 场景 → 角色 → 本镜 → 负向；
   * 省略 `Image N` 参考图行 —— 那些 URL 每次现签、会过期，不能进投影。
   *
   * 可选：老调用方 / 第三方 mock 不传时前端按空串处理（画布显示「提示词待生成」，
   * 而不是整块 hover 消失）。真实投影（`buildStoryboardView`）总会给值。
   */
  promptPreview: z.string().optional(),
});
export type ShotView = z.infer<typeof ShotView>;

export const SceneView = z.object({
  id: PrefixedId,
  order: z.number().int(),
  name: z.string(),
  time: z.string(),
  shotIds: z.array(PrefixedId),
});
export type SceneView = z.infer<typeof SceneView>;

export const EpisodeViewRef = z.object({
  id: PrefixedId,
  no: z.number().int(),
  title: z.string(),
  aspect: Aspect,
});
export type EpisodeViewRef = z.infer<typeof EpisodeViewRef>;

export const StoryboardView = z.object({
  projectId: PrefixedId,
  episode: EpisodeViewRef,
  scenes: z.array(SceneView),
  shots: z.array(ShotView),
  stats: z.object({
    shotCount: z.number().int(),
    approvedCount: z.number().int(),
    usedPoints: z.number().int(),
  }),
  rev: z.number().int(),
});
export type StoryboardView = z.infer<typeof StoryboardView>;

export const ViewKind = z.enum(["storyboard", "canvas", "library", "document", "world", "outline", "script", "terms"]);
export type ViewKind = z.infer<typeof ViewKind>;

// ---------------------------------------------------------------- 文件

export const FileMeta = z.object({
  path: z.string(),
  bytes: z.number().int(),
  sha256: z.string(),
  rev: z.number().int(),
  updatedAt: UnixMs,
});
export type FileMeta = z.infer<typeof FileMeta>;

export const PutFileInput = z.object({
  content: z.string(),
  baseRev: z.number().int(),
});
export type PutFileInput = z.infer<typeof PutFileInput>;

export const PutFileOutput = z.object({ rev: z.number().int(), sha256: z.string(), bytes: z.number().int() });
export type PutFileOutput = z.infer<typeof PutFileOutput>;

export const HistoryItem = z.object({
  rev: z.number().int(),
  /** 该版本对应的文件路径（M4 起必带；老数据缺失时为 null） */
  path: z.string().nullable().optional(),
  sha256: z.string(),
  bytes: z.number().int(),
  source: z.enum(["ui", "agent", "import", "tool"]),
  actor: z.string(),
  createdAt: UnixMs,
});
export type HistoryItem = z.infer<typeof HistoryItem>;

export const HistoryList = z.object({
  items: z.array(HistoryItem),
  nextCursor: z.string().nullable().optional(),
});
export type HistoryList = z.infer<typeof HistoryList>;

/** M1 允许写的路径（其余只读）。前后端共用这一条规则。 */
export const WRITABLE_PATH_PATTERNS: readonly RegExp[] = [
  /^notes\/[\w./-]+\.(md|txt|json)$/,
  /^episodes\/[\w-]+\/(outline|script|storyboard)\.md$/,
  /^project\.json$/,
];

export function isWritablePath(path: string): boolean {
  const p = path.replace(/^\/+/, "");
  return WRITABLE_PATH_PATTERNS.some((re) => re.test(p));
}

// ---------------------------------------------------------------- 实时事件（M2）

export const ProjectEventType = z.enum([
  "snapshot",
  "shot.updated",
  "shot.created",
  "shot.deleted",
  "shot.reordered",
  "scene.updated",
  "media.ready",
  "job.updated",
  "agent.delta",
  "agent.entry",
  "agent.done",
  "checkpoint.ready",
  "world.updated",
  "outline.updated",
  "script.updated",
  "terms.updated",
  "quota.warning",
  "ping",
]);
export type ProjectEventType = z.infer<typeof ProjectEventType>;

export const ProjectEvent = z.object({
  type: ProjectEventType,
  rev: z.number().int(),
  projectId: PrefixedId,
  payload: z.record(z.string(), z.unknown()),
});
export type ProjectEvent = z.infer<typeof ProjectEvent>;

// ---------------------------------------------------------------- 常量

export const FREE_QUOTA = {
  mediaBytes: 1_073_741_824,
  projectLimit: 1,
} as const;

export const SESSION_COOKIE = "dp_session";
export const SESSION_TTL_MS = 30 * 24 * 3600 * 1000;


// ---------------------------------------------------------------- M3：故事板文档（落盘形状）

/** 落盘的 shot：媒体存 key，不存 URL（URL 是投影时现签的） */
export const StoryboardShotDoc = z.object({
  id: PrefixedId,
  sceneId: PrefixedId,
  order: z.number().int().nonnegative(),
  framing: z.string().min(1).max(20),
  duration: z.number().min(0.5).max(15),
  action: z.string().max(500).default(""),
  status: ShotStatus.default("draft"),
  dialogue: z.array(DialogueLine).max(6).default([]),
  chars: z.array(PrefixedId).max(6).default([]),
  keyframe: z.string().nullable().default(null),
  endFrame: z.string().nullable().default(null),
  clip: z.string().nullable().default(null),
  checks: z.array(ShotCheck).default([]),
  costPoints: z.number().int().nonnegative().default(0),
});
export type StoryboardShotDoc = z.infer<typeof StoryboardShotDoc>;

export const StoryboardSceneDoc = z.object({
  id: PrefixedId,
  order: z.number().int().nonnegative(),
  name: z.string().min(1).max(40),
  time: z.string().max(20).default(""),
});
export type StoryboardSceneDoc = z.infer<typeof StoryboardSceneDoc>;

/**
 * `episodes/{ep}/storyboard.json` 的完整形状。
 * **id 必须由调用方给**（Agent 用 `scn_s1` / `sht_s1` 这种确定性 id 就行）：
 * 服务端不做 id 生成，因为镜头靠 `sceneId` 互相引用，服务端另起 id 反而要重写引用。
 */
export const StoryboardDoc = z.object({
  episode: EpisodeViewRef,
  scenes: z.array(StoryboardSceneDoc).max(200),
  shots: z.array(StoryboardShotDoc).max(1000),
});
export type StoryboardDoc = z.infer<typeof StoryboardDoc>;

// ---------------------------------------------------------------- M2：命令层

export const ShotPatch = z.object({
  framing: z.string().min(1).max(20).optional(),
  /** 用哪套配方生成（M5 起） */
  recipeId: PrefixedId.optional(),
  duration: z.number().min(0.5).max(15).optional(),
  action: z.string().max(500).optional(),
  status: ShotStatus.optional(),
  charIds: z.array(PrefixedId).optional(),
});
export type ShotPatch = z.infer<typeof ShotPatch>;

export const ScenePatch = z.object({
  name: z.string().min(1).max(40).optional(),
  time: z.string().max(20).optional(),
});
export type ScenePatch = z.infer<typeof ScenePatch>;

export const UpdateShotCommand = z.object({ type: z.literal("update_shot"), shotId: PrefixedId, patch: ShotPatch });
export const SetDialogueCommand = z.object({
  type: z.literal("set_dialogue"),
  shotId: PrefixedId,
  lines: z.array(DialogueLine).max(6),
});
export const AddShotCommand = z.object({
  type: z.literal("add_shot"),
  sceneId: PrefixedId,
  afterShotId: PrefixedId.optional(),
  shot: z.object({
    framing: z.string().min(1).max(20),
    duration: z.number().min(0.5).max(15),
    action: z.string().max(500),
    dialogue: z.array(DialogueLine).max(6).default([]),
    chars: z.array(PrefixedId).default([]),
  }),
});
export const RemoveShotCommand = z.object({ type: z.literal("remove_shot"), shotId: PrefixedId });
export const ReorderShotsCommand = z.object({
  type: z.literal("reorder_shots"),
  sceneId: PrefixedId,
  orderedIds: z.array(PrefixedId).min(1),
});
export const UpdateSceneCommand = z.object({ type: z.literal("update_scene"), sceneId: PrefixedId, patch: ScenePatch });

/**
 * 整篇替换（M3）：新项目是空的，而 update/add/remove 都只能改已有内容，
 * 所以「从剧本生成故事板」必须有入口。走命令层而不是直写文件，是为了复用
 * rev / 事件 / 版本链 —— Agent 和 UI 依然同一条路径。
 * 语义：整篇覆盖（不是合并），`doc` 必须自洽（id 唯一、sceneId 引用存在）。
 */
// ---------------------------------------------------------------- M7：世界树 / 大纲 / 剧本 / 术语

export const WorldLevel = z.enum(["world", "region", "state", "place", "scene"]);
export type WorldLevel = z.infer<typeof WorldLevel>;

/**
 * 设定图用途。单独导出是为了让「上传接口校验 kind」与「前端分组」共用同一份值域
 * ——两边各写一份枚举，迟早会出现「前端能选、后端 422」。
 */
export const WorldImageKind = z.enum(["景观", "全景", "正", "反", "左", "右", "站位"]);
export type WorldImageKind = z.infer<typeof WorldImageKind>;

export const WorldImage = z.object({
  kind: WorldImageKind,
  /** R2 key；投影里另给 url */
  key: z.string(),
  url: z.string().optional(),
});
export type WorldImage = z.infer<typeof WorldImage>;

export const WorldNode = z.object({
  id: PrefixedId,
  name: z.string().min(1).max(40),
  level: WorldLevel,
  parentId: PrefixedId.nullable(),
  order: z.number().int(),
  summary: z.string().max(2000).nullable(),
  /** 别名（搜索与引用匹配都用） */
  aliases: z.array(z.string().max(40)).max(10).default([]),
  /** 区域分组（原型里有，用于树的分段着色） */
  group: z.string().max(40).nullable().optional(),
  /** 复现章节说明，如「第11话首见；第12话再出现」 */
  chaptersNote: z.string().max(200).nullable().optional(),
  /** 首见章节（集号） */
  firstChapter: z.number().int().nullable(),
  images: z.array(WorldImage).max(12),
  /** 该节点出现在哪些产出线 */
  outputs: z.array(z.string()).max(12),
  /** 直接挂在该节点下、可直接开拍的场景（storyboard 里的 scene.id） */
  sceneIds: z.array(PrefixedId).max(50),
});
export type WorldNode = z.infer<typeof WorldNode>;

export const WorldRuleVersion = z.object({
  version: z.string().max(16),
  note: z.string().max(200).nullable(),
  createdAt: UnixMs,
});
export type WorldRuleVersion = z.infer<typeof WorldRuleVersion>;

export const WorldRule = z.object({
  id: PrefixedId,
  title: z.string().min(1).max(40),
  category: z.string().max(20),
  /** 一句话摘要（列表卡用） */
  summary: z.string().max(200).nullable().optional(),
  /** 相关条目名（节点/术语） */
  related: z.array(z.string().max(40)).max(20).default([]),
  /** 适用范围：全项目，或指定产出线 */
  scope: z.object({ all: z.boolean(), outputIds: z.array(z.string()).max(12) }),
  body: z.string().max(8000),
  version: z.string().max(16),
  versions: z.array(WorldRuleVersion).max(20),
});
export type WorldRule = z.infer<typeof WorldRule>;

export const WorldView = z.object({
  projectId: PrefixedId,
  rev: z.number().int(),
  nodes: z.array(WorldNode),
  rules: z.array(WorldRule),
});
export type WorldView = z.infer<typeof WorldView>;

/**
 * 递归树节点：显式标注输出类型，避免 `z.lazy` 自引用把推断打成 any
 * （TS7022，会让下游 children.map 全部隐式 any）。
 */
export type OutlineStatus = "done" | "current" | "next";

export interface OutlineNodeData {
  id: string;
  title: string;
  /** 副标题：如「第11话 · 16镜」 */
  meta?: string | null;
  summary?: string | null;
  /** 进度：已写完 / 正在写 / 待写 */
  status?: OutlineStatus;
  /** 集级节点对应的集 */
  episodeId?: string | null;
  outline: string;
  children: OutlineNodeData[];
}

export const OutlineNode: z.ZodType<OutlineNodeData> = z.object({
  id: PrefixedId,
  title: z.string().min(1).max(60),
  meta: z.string().max(120).nullable().optional(),
  summary: z.string().max(500).nullable().optional(),
  status: z.enum(["done", "current", "next"]).optional(),
  episodeId: PrefixedId.nullable().optional(),
  /** 真实正文，不是一句摘要 */
  outline: z.string().max(20000),
  children: z.array(z.lazy(() => OutlineNode)).max(50),
});
export type OutlineNode = z.infer<typeof OutlineNode>;

export const OutlineView = z.object({
  projectId: PrefixedId,
  rev: z.number().int(),
  root: OutlineNode,
});
export type OutlineView = z.infer<typeof OutlineView>;

export const ScriptBeat = z.object({ no: z.string().max(8), title: z.string().max(40).optional(), text: z.string().max(500) });
export const ScriptScene = z.object({
  id: PrefixedId,
  name: z.string().max(40),
  time: z.string().max(10),
  summary: z.string().max(1000).nullable().optional(),
});
export type ScriptScene = z.infer<typeof ScriptScene>;
export const ScriptRow = z.object({
  no: z.string().max(8),
  sceneId: PrefixedId,
  /** 版式：满版 / 单格 / 正反打 / 特写 CU 等 */
  layout: z.string().max(20),
  framing: z.string().max(20),
  duration: z.number().min(0.5).max(60),
  action: z.string().max(1000),
  dialogue: z.string().max(1000),
  /** 对应到故事板的镜头（有就能精确跳转，没有只能定位到场景） */
  shotId: PrefixedId.nullable().optional(),
  sceneName: z.string().max(40).optional(),
});
export type ScriptRow = z.infer<typeof ScriptRow>;

export const ScriptDoc = z.object({
  /** 来源说明：改编自哪个文件/章节 */
  source: z.string().max(200).nullable().optional(),
  beats: z.array(ScriptBeat).max(200),
  scenes: z.array(ScriptScene).max(100),
  rows: z.array(ScriptRow).max(500),
});
export type ScriptDoc = z.infer<typeof ScriptDoc>;

export const ScriptView = z.object({
  projectId: PrefixedId,
  rev: z.number().int(),
  script: ScriptDoc,
});
export type ScriptView = z.infer<typeof ScriptView>;

export const Term = z.object({
  id: PrefixedId,
  term: z.string().min(1).max(40),
  aliases: z.array(z.string().max(40)).max(10),
  category: z.string().max(20),
  definition: z.string().max(4000),
});
export type Term = z.infer<typeof Term>;

export const TermList = z.object({
  projectId: PrefixedId,
  rev: z.number().int(),
  items: z.array(Term),
});
export type TermList = z.infer<typeof TermList>;

export const ReplaceStoryboardCommand = z.object({
  type: z.literal("replace_storyboard"),
  doc: StoryboardDoc,
});

/**
 * M7：文档类命令。与 `replace_storyboard` 同一套闸门（整篇覆盖 + rev + 版本链 + 事件），
 * 所以世界树 / 大纲 / 剧本 / 术语天然具备撤销、历史与检查点。
 */
export const ReplaceWorldCommand = z.object({
  type: z.literal("replace_world"),
  nodes: z.array(WorldNode).max(500),
});
export const ReplaceWorldRulesCommand = z.object({
  type: z.literal("replace_world_rules"),
  rules: z.array(WorldRule).max(200),
});
export const ReplaceOutlineCommand = z.object({
  type: z.literal("replace_outline"),
  root: OutlineNode,
});
export const ReplaceScriptCommand = z.object({
  type: z.literal("replace_script"),
  script: ScriptDoc,
});
export const ReplaceTermsCommand = z.object({
  type: z.literal("replace_terms"),
  items: z.array(Term).max(500),
});

export const Command = z.discriminatedUnion("type", [
  UpdateShotCommand,
  SetDialogueCommand,
  AddShotCommand,
  RemoveShotCommand,
  ReorderShotsCommand,
  UpdateSceneCommand,
  ReplaceWorldCommand,
  ReplaceWorldRulesCommand,
  ReplaceOutlineCommand,
  ReplaceScriptCommand,
  ReplaceTermsCommand,
  ReplaceStoryboardCommand,
]);
export type Command = z.infer<typeof Command>;

export const CommandBatchInput = z.object({
  baseRev: z.number().int().nonnegative(),
  /** 缺省为 ep01（M2 单集）；M3 起多集必须显式带上 */
  episodeId: PrefixedId.optional(),
  commands: z.array(Command).min(1).max(200),
});
export type CommandBatchInput = z.infer<typeof CommandBatchInput>;

export const CommandBatchOutput = z.object({
  rev: z.number().int(),
  applied: z.number().int(),
  events: z.array(ProjectEvent),
});
export type CommandBatchOutput = z.infer<typeof CommandBatchOutput>;

// ---------------------------------------------------------------- M2：Agent 会话

export const AgentEntryKind = z.enum(["user", "assistant", "tool", "system"]);
export type AgentEntryKind = z.infer<typeof AgentEntryKind>;

export const AgentEntry = z.object({
  id: z.string(),
  kind: AgentEntryKind,
  text: z.string(),
  /** tool 专用：工具名、参数、结果状态 */
  name: z.string().optional(),
  args: z.record(z.string(), z.unknown()).optional(),
  status: z.enum(["running", "ok", "error"]).optional(),
  at: UnixMs,
});
export type AgentEntry = z.infer<typeof AgentEntry>;

export const AgentView = z.object({
  sessionId: z.string(),
  busy: z.boolean(),
  entries: z.array(AgentEntry),
  pending: z.array(z.string()),
  usage: z.object({
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    points: z.number().int(),
  }),
});
export type AgentView = z.infer<typeof AgentView>;

export const SendAgentMessageInput = z.object({
  text: z.string().min(1).max(4000),
  attachments: z
    .array(z.object({ path: z.string().regex(/^inbox\/[\w.-]+$/), kind: z.enum(["image", "text", "audio", "video"]) }))
    .max(9)
    .default([]),
});
export type SendAgentMessageInput = z.infer<typeof SendAgentMessageInput>;

export const SendAgentMessageOutput = z.object({
  operationId: z.string(),
  sessionId: z.string(),
  accepted: z.boolean(),
});
export type SendAgentMessageOutput = z.infer<typeof SendAgentMessageOutput>;

// ---------------------------------------------------------------- M2：上传

export const UploadOutput = z.object({
  path: z.string(),
  bytes: z.number().int(),
  contentType: z.string(),
});
export type UploadOutput = z.infer<typeof UploadOutput>;

// ---------------------------------------------------------------- M3：生成流水线

export const GenerationKind = z.enum(["keyframe", "endframe", "clip"]);
export type GenerationKind = z.infer<typeof GenerationKind>;

export const GenerationStatus = z.enum(["queued", "running", "partial", "succeeded", "failed", "cancelled"]);
export type GenerationStatus = z.infer<typeof GenerationStatus>;

/** 单项状态：没有 partial —— 一个 item 要么成了要么没成 */
export const GenerationItemStatus = z.enum(["queued", "running", "succeeded", "failed", "cancelled"]);
export type GenerationItemStatus = z.infer<typeof GenerationItemStatus>;

export const GenerationItem = z.object({
  id: z.string(),
  shotId: PrefixedId,
  status: GenerationItemStatus,
  media: MediaRef.nullable(),
  points: z.number().int(),
  error: z.string().nullable(),
});
export type GenerationItem = z.infer<typeof GenerationItem>;

export const GenerationJob = z.object({
  id: PrefixedId,
  projectId: PrefixedId,
  kind: GenerationKind,
  status: GenerationStatus,
  items: z.array(GenerationItem),
  estimatedPoints: z.number().int(),
  reservedPoints: z.number().int(),
  settledPoints: z.number().int(),
  refundedPoints: z.number().int(),
  createdAt: UnixMs,
  updatedAt: UnixMs,
});
export type GenerationJob = z.infer<typeof GenerationJob>;

export const CreateGenerationInput = z.object({
  kind: GenerationKind,
  shotIds: z.array(PrefixedId).min(1).max(50),
  model: z.string().max(60).optional(),
  options: z
    .object({
      size: z.string().max(20).optional(),
      quality: z.enum(["standard", "high"]).optional(),
      seconds: z.number().min(1).max(15).optional(),
      resolution: z.enum(["480p", "720p", "1080p"]).optional(),
    })
    .optional(),
});
export type CreateGenerationInput = z.infer<typeof CreateGenerationInput>;

export const CreateGenerationOutput = z.object({
  jobId: PrefixedId,
  estimatedPoints: z.number().int(),
  reservedPoints: z.number().int(),
  status: GenerationStatus,
  /**
   * 生成前的非阻断提示（契约《参考图生成》§7）：例如某些角色没有参考图、
   * 一致性不保证。缺省为空数组 —— 老客户端解析不会因为多了这个字段失败。
   */
  warnings: z.array(z.string()).default([]),
});
export type CreateGenerationOutput = z.infer<typeof CreateGenerationOutput>;

/** 前置核查里「这个角色没有参考图」（契约《参考图生成》§7）。 */
export const PreflightMissingCharacter = z.object({
  charId: z.string(),
  name: z.string(),
});
export type PreflightMissingCharacter = z.infer<typeof PreflightMissingCharacter>;

export const GenerationPreflightShot = z.object({
  shotId: PrefixedId,
  missing: z.array(PreflightMissingCharacter),
});
export type GenerationPreflightShot = z.infer<typeof GenerationPreflightShot>;

/**
 * `GET /generations/preflight` 的响应：生成前可核查「参考图是否齐备」。
 * `ready = missingCharacters.length === 0`；生成接口不因 false 阻断（用户可能先粗出）。
 */
export const GenerationPreflight = z.object({
  ready: z.boolean(),
  shots: z.array(GenerationPreflightShot),
  missingCharacters: z.array(PreflightMissingCharacter),
});
export type GenerationPreflight = z.infer<typeof GenerationPreflight>;

export const GenerationList = z.object({
  items: z.array(GenerationJob),
  nextCursor: z.string().nullable(),
});
export type GenerationList = z.infer<typeof GenerationList>;

export const EstimateOutput = z.object({
  estimatedPoints: z.number().int(),
  breakdown: z.array(z.object({ shotId: PrefixedId, points: z.number().int() })),
  /** 本次估算实际采用的默认档位；前端在确认层要显示出来，避免用户不知道按什么算的钱 */
  defaults: z
    .object({
      seconds: z.number().optional(),
      resolution: z.string().optional(),
      size: z.string().optional(),
    })
    .optional(),
});
export type EstimateOutput = z.infer<typeof EstimateOutput>;

// ---------------------------------------------------------------- M4：撤销 / 配额 / 导出

/** 回滚：把指定路径恢复到 toRev 时的内容，**产生一个新 rev**（不是删历史） */
export const RevertInput = z.object({
  baseRev: z.number().int().nonnegative(),
  toRev: z.number().int().nonnegative(),
  /** 省略 = 整个项目回滚 */
  paths: z.array(z.string()).min(1).optional(),
});
export type RevertInput = z.infer<typeof RevertInput>;

export const RevertOutput = z.object({
  rev: z.number().int(),
  restored: z.array(z.string()),
  events: z.array(ProjectEvent),
});
export type RevertOutput = z.infer<typeof RevertOutput>;

/**
 * Colab CU 用量（M7）。
 *
 * 背景：用户成本有两块 —— Drama Pie 点数 + **他 Colab 账号里的 CU**。后者是我们在调，
 * 但 Colab 不提供按任务/按账号的查询接口；用差值算会把他在别处的消费归因过来，
 * 并发跑时更算不出每个任务对应多少。
 *
 * 所以只报**能确证的事实**：虚拟机开了多久（boot → 回收）。CU 是按机型单价**估算**的，
 * 明确标注 estimated，且必须告诉用户「这部分不扣 Drama Pie 点数」。
 */
export const ColabUsageSession = z.object({
  sessionId: z.string(),
  /** 机型，如 gpu-g4-s-ft-use5c1 */
  gpu: z.string(),
  startedAt: UnixMs,
  endedAt: UnixMs.nullable(),
  /** 运行秒数 = endedAt - startedAt（未回收时按 now 算） */
  runtimeSeconds: z.number().int(),
  /** 这台机器上跑了几个任务 */
  taskCount: z.number().int(),
  /** 这台机器服务的项目（按任务归属） */
  projectIds: z.array(z.string()),
  /** 估算 CU = runtimeSeconds / 3600 * cuPerHour */
  estimatedCu: z.number(),
});
export type ColabUsageSession = z.infer<typeof ColabUsageSession>;

export const ColabUsage = z.object({
  sessions: z.array(ColabUsageSession),
  totalRuntimeSeconds: z.number().int(),
  estimatedCu: z.number(),
  /** 估算 CNY，按 cnyPerCu 折算；前端展示时标注「估算」 */
  estimatedCny: z.number(),
  pricingBasis: z.object({
    gpu: z.string(),
    cuPerHour: z.number(),
    cnyPerCu: z.number(),
    /** 口径来源，如 "colab 2026-10 实测 8.4 CU/h, 1 CU ≈ ¥0.72" */
    note: z.string(),
  }),
  /** 恒为 user_colab：这部分钱花在用户的 Colab 账号上，不扣 Drama Pie 点数 */
  billedTo: z.literal("user_colab"),
});
export type ColabUsage = z.infer<typeof ColabUsage>;

export const UsageSummary = z.object({
  media: z.object({ usedBytes: z.number().int(), limitBytes: z.number().int() }),
  projects: z.object({ used: z.number().int(), limit: z.number().int() }),
  points: z.object({ used: z.number().int(), balance: z.number().int() }),
  byProject: z.array(
    z.object({
      projectId: PrefixedId,
      title: z.string(),
      mediaBytes: z.number().int(),
      points: z.number().int(),
    }),
  ),
  overLimit: z.boolean(),
  /**
   * Colab 侧用量。没有跑过 H3 时为 null。
   * **不要**把它加进 `points`——那是两个账户的两笔钱。
   */
  colab: ColabUsage.nullable().optional(),
});
export type UsageSummary = z.infer<typeof UsageSummary>;

export const ExportKind = z.enum(["zip", "pdf_storyboard"]);
export type ExportKind = z.infer<typeof ExportKind>;

export const ExportStatus = z.enum(["queued", "running", "succeeded", "failed"]);
export type ExportStatus = z.infer<typeof ExportStatus>;

export const CreateExportInput = z.object({
  kind: ExportKind,
  episodes: z.array(PrefixedId).min(1).optional(),
  includeMedia: z.boolean().default(true),
});
export type CreateExportInput = z.infer<typeof CreateExportInput>;

export const ExportJob = z.object({
  id: PrefixedId,
  projectId: PrefixedId,
  kind: ExportKind,
  status: ExportStatus,
  bytes: z.number().int().nullable(),
  url: z.string().nullable(),
  expiresAt: UnixMs.nullable(),
  error: z.string().nullable(),
  createdAt: UnixMs,
  updatedAt: UnixMs,
});
export type ExportJob = z.infer<typeof ExportJob>;

export const CreateExportOutput = z.object({ exportId: PrefixedId, status: ExportStatus });
export type CreateExportOutput = z.infer<typeof CreateExportOutput>;

export const ExportList = z.object({ items: z.array(ExportJob) });
export type ExportList = z.infer<typeof ExportList>;

/** 撤销就是"不带 paths 的回滚"，响应同构 */
export const UndoOutput = RevertOutput;
export type UndoOutput = z.infer<typeof UndoOutput>;

// ---------------------------------------------------------------- M5：资料库 / 分享 / 检查点 / 配方

export const LibraryKind = z.enum(["character", "location", "prop"]);
export type LibraryKind = z.infer<typeof LibraryKind>;

export const LibraryEntity = z.object({
  id: PrefixedId,
  kind: LibraryKind,
  name: z.string().min(1).max(40),
  /** 角色定位（主角/配角/龙套）；场景为空 */
  role: z.string().max(20).nullable(),
  /** 中文外形描述，给人和模型看 */
  look: z.string().max(500).nullable(),
  /** 英文外形描述，直接进提示词 */
  lookEn: z.string().max(500).nullable(),
  /** 场景的时间段（清晨/日/夜…） */
  time: z.array(z.string().max(10)).max(6),
  /** 参考图的媒体 key（不是 URL，URL 现签） */
  refs: z.array(z.string()).max(12),
  /**
   * 与 refs 一一对应的**现签 URL**（10 分钟 TTL）。
   * 投影里给 URL、存储里给 key —— 和 `MediaRef` 同口径，前端不要拿 key 拼地址。
   */
  refUrls: z.array(z.string()).optional(),
  notes: z.string().max(1000).nullable(),
  /** 当前版本号，如 v3 */
  version: z.string().max(16).nullable().optional(),
  versions: z.array(z.object({
    version: z.string().max(16),
    note: z.string().max(200).nullable(),
    createdAt: UnixMs,
  })).max(20).optional(),
  createdAt: UnixMs,
  updatedAt: UnixMs,
});
export type LibraryEntity = z.infer<typeof LibraryEntity>;

export const LibraryList = z.object({ items: z.array(LibraryEntity) });
export type LibraryList = z.infer<typeof LibraryList>;

export const CreateLibraryInput = z.object({
  kind: LibraryKind,
  name: z.string().min(1).max(40),
  role: z.string().max(20).optional(),
  look: z.string().max(500).optional(),
  lookEn: z.string().max(500).optional(),
  time: z.array(z.string().max(10)).max(6).optional(),
  notes: z.string().max(1000).optional(),
});
export type CreateLibraryInput = z.infer<typeof CreateLibraryInput>;

export const PatchLibraryInput = CreateLibraryInput.partial().omit({ kind: true });
export type PatchLibraryInput = z.infer<typeof PatchLibraryInput>;

export const ShareScope = z.enum(["storyboard", "episode"]);
export type ShareScope = z.infer<typeof ShareScope>;

export const CreateShareInput = z.object({
  scope: ShareScope,
  episodeId: PrefixedId.optional(),
  expiresInDays: z.number().int().min(1).max(30).default(7),
  allowDownload: z.boolean().default(false),
});
export type CreateShareInput = z.infer<typeof CreateShareInput>;

export const ShareLink = z.object({
  /** 稳定 id：列表里也有它，撤销用它——**不要靠明文 token 撤销**（列表只回掩码） */
  id: PrefixedId,
  token: z.string(),
  url: z.string(),
  scope: ShareScope,
  episodeId: PrefixedId.nullable(),
  allowDownload: z.boolean(),
  expiresAt: UnixMs,
  createdAt: UnixMs,
});
export type ShareLink = z.infer<typeof ShareLink>;

export const ShareList = z.object({ items: z.array(ShareLink) });
export type ShareList = z.infer<typeof ShareList>;

export const CheckpointStatus = z.enum(["queued", "running", "ready", "failed"]);
export type CheckpointStatus = z.infer<typeof CheckpointStatus>;

export const Checkpoint = z.object({
  id: PrefixedId,
  commit: z.string().nullable(),
  message: z.string().max(200),
  tag: z.string().max(40).nullable(),
  files: z.number().int(),
  bytes: z.number().int(),
  media: z.object({ count: z.number().int(), bytes: z.number().int() }),
  status: CheckpointStatus,
  error: z.string().nullable(),
  createdAt: UnixMs,
});
export type Checkpoint = z.infer<typeof Checkpoint>;

export const CheckpointList = z.object({ items: z.array(Checkpoint) });
export type CheckpointList = z.infer<typeof CheckpointList>;

export const CreateCheckpointInput = z.object({
  message: z.string().min(1).max(200),
  tag: z.string().max(40).optional(),
});
export type CreateCheckpointInput = z.infer<typeof CreateCheckpointInput>;

export const Recipe = z.object({
  id: PrefixedId,
  name: z.string().min(1).max(40),
  appliesTo: z.array(z.enum(["keyframe", "endframe", "clip", "storyboard"])).min(1),
  /** 提示词模板：按 kind 分组，占位符用 {scene} {char} {action} 等 */
  templates: z.record(z.string(), z.string().max(4000)),
  params: z.record(z.string(), z.string().max(500)),
  createdAt: UnixMs,
  updatedAt: UnixMs,
});
export type Recipe = z.infer<typeof Recipe>;

export const RecipeList = z.object({ items: z.array(Recipe) });
export type RecipeList = z.infer<typeof RecipeList>;

// ---------------------------------------------------------------- M6：只读画布 / 时间线

export const CanvasRect = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
export type CanvasRect = z.infer<typeof CanvasRect>;

/** 画布上的一个镜头卡（与故事板同一份 ShotView，不重复定义字段） */
export const CanvasShotNode = z.object({
  shotId: PrefixedId,
  rect: CanvasRect,
  shot: ShotView,
});
export type CanvasShotNode = z.infer<typeof CanvasShotNode>;

/** 场是一个虚线容器，里面按网格摆镜头卡 */
export const CanvasSceneGroup = z.object({
  sceneId: PrefixedId,
  name: z.string(),
  time: z.string(),
  rect: CanvasRect,
  shots: z.array(CanvasShotNode),
});
export type CanvasSceneGroup = z.infer<typeof CanvasSceneGroup>;

export const CanvasView = z.object({
  projectId: PrefixedId,
  episode: EpisodeViewRef,
  rev: z.number().int(),
  /** 每行放几张卡（服务端算，保证不同客户端一致） */
  columns: z.number().int(),
  scenes: z.array(CanvasSceneGroup),
  /** 没有 sceneId 的孤儿镜头 */
  unplaced: z.array(CanvasShotNode),
  viewport: z.object({ x: z.number(), y: z.number(), zoom: z.number() }),
});
export type CanvasView = z.infer<typeof CanvasView>;

export const TimelineClip = z.object({
  shotId: PrefixedId,
  start: z.number(),
  duration: z.number(),
  status: ShotStatus,
  media: MediaRef.nullable(),
  poster: MediaRef.nullable(),
});
export type TimelineClip = z.infer<typeof TimelineClip>;

export const TimelineBlock = z.object({
  shotId: PrefixedId,
  start: z.number(),
  duration: z.number(),
  who: z.string().nullable(),
  text: z.string(),
});
export type TimelineBlock = z.infer<typeof TimelineBlock>;

export const TimelineView = z.object({
  projectId: PrefixedId,
  episode: EpisodeViewRef,
  rev: z.number().int(),
  totalSeconds: z.number(),
  clips: z.array(TimelineClip),
  dialogues: z.array(TimelineBlock),
});
export type TimelineView = z.infer<typeof TimelineView>;
